import { apiClient, handleApiRequest } from './client'

/** Wire contract with the backend: `ConfigService.PASSWORD_MASK`. A secret
 * field containing this value (or empty) in an update is treated as unchanged. */
export const PASSWORD_MASK = '••••••••'

export interface ConfigField {
  key: string
  label: string
  type: 'int' | 'float' | 'bool' | 'str' | 'json_list' | 'enum_list' | 'scope' | 'slider' | 'password' | 'color' | 'custom' | 'role_state'
  default: unknown
  min_value: number | null
  max_value: number | null
  choices: string[] | null
  description: string
  delegatable: boolean
  /** hr2-v2 (E14): deutsche Anzeige-Labels für Choice-Werte (optional;
   * Fallback auf den Roh-Wert, wenn ein Label fehlt). */
  choice_labels?: Record<string, string> | null
}

export interface ConfigSubgroup {
  name: string
  leitfrage: string
  intro: string
  keys: string[]
  highlight?: boolean
}

export interface ConfigResponse {
  config: Record<string, unknown>
  fields: ConfigField[]
  categories: ConfigSubgroup[]
  description: string
  label: string
  /** Direktorkonfig v2 (D11): nur gesetzt, wenn ein Direktor liest —
   * per-Person visible/editable je Feld (inkl. 'absence_types'-Karte). */
  field_permissions?: Record<string, { visible: boolean; editable: boolean }> | null
  /** hr2-v2 (K1/E6): nur für ct='hr' — Rollen-Delegation für HR aus den
   * setting.*-Scopes. Bewusst ein eigener Kanal (KEIN Lese-Filter wie
   * field_permissions): der Superadmin-Editor zeigt alle Zeilen, dieser
   * Zustand steuert nur das 3-State-Segment / FieldDelegationControl. */
  delegation?: { role: string; permissions: Record<string, { visible: boolean; editable: boolean }> } | null
}

export interface ConfigTypeInfo {
  key: string
  name: string
  description: string
}

export interface PermissionState {
  visible: boolean
  editable: boolean
  min_value: number | null
  max_value: number | null
}

export const configApi = {
  getConfig: (configType: string) =>
    handleApiRequest<ConfigResponse>(
      apiClient.get(`/api/v1/config/${configType}`)
    ),

  updateConfig: (configType: string, data: Record<string, unknown>, scopes?: Record<string, unknown>) =>
    handleApiRequest<Record<string, unknown>>(
      apiClient.put(`/api/v1/config/${configType}`, { data, ...(scopes ? { scopes } : {}) })
    ),

  testEmail: () =>
    handleApiRequest<{ success: boolean; mode: string; message: string; recipient_email: string }>(
      apiClient.post('/api/v1/config/email/test')
    ),

  getConfigTypes: () =>
    handleApiRequest<{ types: ConfigTypeInfo[] }>(
      apiClient.get('/api/v1/config/types')
    ),

  getPermissions: (configType: string) =>
    handleApiRequest<{ permissions: Record<string, PermissionState> }>(
      apiClient.get(`/api/v1/config/${configType}/permissions`)
    ),

  /** Omitted keys keep stored values; explicit null clears min/max. */
  updatePermissions: (configType: string, permissions: Record<string, { visible?: boolean; editable?: boolean; min_value?: number | null; max_value?: number | null }>) =>
    handleApiRequest<{ updated: number; permissions: Record<string, PermissionState> }>(
      apiClient.put(`/api/v1/config/${configType}/permissions`, permissions)
    ),

  // Direktorkonfig v2 (D12/D16): per-Direktor-Rechte-Matrix der
  // Director-Einstellungen (Superadmin-only).
  getDirectorPermissions: (directorId: string) =>
    handleApiRequest<{ director_id: string; permissions: Record<string, { visible: boolean; editable: boolean }> }>(
      apiClient.get(`/api/v1/config/director/director-permissions?director_id=${encodeURIComponent(directorId)}`)
    ),

  updateDirectorPermissions: (directorId: string, permissions: Record<string, { visible: boolean; editable: boolean }>) =>
    handleApiRequest<{ updated: number; director_id: string; permissions: Record<string, { visible: boolean; editable: boolean }> }>(
      apiClient.put(`/api/v1/config/director/director-permissions?director_id=${encodeURIComponent(directorId)}`, permissions)
    ),
}
